import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BASE_URL from "../../BASEURL";

export default function ReleaseEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [release, setRelease] = useState({
    Release_name: "",
    Release_photo: "",
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchRelease = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/releases/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch release');
        }
        const data = await response.json();
        setRelease(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRelease();
  }, [id]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError("Please select a valid image (JPEG, PNG, GIF) or PDF file.");
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);

    const formData = new FormData();
    formData.append('Release_name', release.Release_name);
    if (file) {
      formData.append('Release_photo', file);
    }

    try {
      const response = await fetch(`${BASE_URL}/api/releases/${id}`, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update release: ${response.status} ${errorText}`);
      }

      navigate("/admin/ReleaseList");
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return (
    <div className="p-6 flex justify-center items-center min-h-screen bg-light-grey">
      <div className="text-gold text-xl">Loading...</div>
    </div>
  );
  if (error) return (
    <div className="p-6 flex justify-center items-center min-h-screen bg-light-grey">
      <div className="text-red-500 text-xl">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-6 bg-light-grey min-h-screen">

      {/* PAGE TITLE */}
      <h1 className="text-3xl font-bold text-deep-black mb-8">
        Edit Release
      </h1>

      {/* FORM CONTAINER */}
      <div className="bg-white shadow-lg rounded-2xl p-8 max-w-2xl mx-auto">

        {/* ERROR MESSAGE */}
        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-6 border border-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Release Name */}
          <div>
            <label className="block mb-2 text-deep-black font-semibold text-lg">
              Release Name
            </label>
            <input
              type="text"
              value={release.Release_name}
              onChange={(e) => setRelease({ ...release, Release_name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent transition-all duration-200"
              placeholder="Enter release name"
              required
            />
          </div>

          {/* Release Photo */}
          <div>
            <label className="block mb-2 text-deep-black font-semibold text-lg">
              Release Photo ( Image )
            </label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent transition-all duration-200"
            />
            {file && <p className="text-sm text-gray-600 mt-1">Selected: {file.name}</p>}
            {release.Release_photo && !file && (
              <p className="text-sm text-gray-600 mt-1">
                Current: <a href={`${release.Release_photo}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">View File</a>
              </p>
            )}
          </div>

          {/* BUTTONS */}
          <div className="flex justify-end gap-4 pt-4">

            {/* CANCEL */}
            <button
              type="button"
              onClick={() => navigate("/admin/ReleaseList")}
              className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
            >
              Cancel
            </button>

            {/* UPDATE */}
            <button
              type="submit"
              disabled={updating}
              className="px-6 py-3 rounded-lg bg-gold text-deep-black font-semibold hover:bg-gold-dark transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? "Updating..." : "Update Release"}
            </button>

          </div>

        </form>

      </div>
    </div>
  );
}